import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Building, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankAccountsPage = () => {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_holder: '',
    iban: '',
    is_active: true
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true });
      setAccounts(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axios.put(`${API_URL}/api/bank-accounts/${editingAccount.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/bank-accounts`, formData, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      bank_name: account.bank_name || '',
      account_holder: account.account_holder || '',
      iban: account.iban || '',
      is_active: account.is_active ?? true
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/bank-accounts/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchAccounts();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      bank_name: '',
      account_holder: '',
      iban: '',
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('bankAccounts')}</h1>
          <p className="text-gray-500 text-sm mt-1">{accounts.length} accounts</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-bank-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addBankAccount')}
        </Button>
      </div>

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((account) => (
          <div 
            key={account.id} 
            className={`bg-white border p-6 card-hover ${account.is_active ? 'border-black' : 'border-gray-200'}`}
            data-testid={`bank-account-${account.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${account.is_active ? 'bg-black' : 'bg-gray-200'}`}>
                  <CreditCard className={`w-5 h-5 ${account.is_active ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h3 className="font-semibold">{account.bank_name}</h3>
                  <p className="text-xs text-gray-500">{account.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={() => handleEdit(account)} 
                  className="p-1 hover:bg-gray-100"
                  data-testid={`edit-bank-account-${account.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(account.id)} 
                  className="p-1 hover:bg-red-100 text-red-600"
                  data-testid={`delete-bank-account-${account.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">{t('accountHolder')}</p>
                <p className="font-medium">{account.account_holder}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">{t('iban')}</p>
                <p className="font-mono text-sm break-all">{account.iban}</p>
              </div>
            </div>
          </div>
        ))}
        
        {accounts.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            {t('noData')}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingAccount ? 'Edit Bank Account' : t('addBankAccount')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('bankName')} *</label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="bank-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('accountHolder')} *</label>
              <Input
                value={formData.account_holder}
                onChange={(e) => setFormData(prev => ({ ...prev, account_holder: e.target.value }))}
                required
                className="rounded-none mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('iban')} *</label>
              <Input
                value={formData.iban}
                onChange={(e) => setFormData(prev => ({ ...prev, iban: e.target.value }))}
                required
                className="rounded-none mt-1 font-mono"
                placeholder="TR00 0000 0000 0000 0000 0000 00"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded-none"
              />
              <label htmlFor="is_active" className="text-sm font-medium">{t('isActive')}</label>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="bank-account-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccountsPage;
